// public/js/utils/idb.js
// Minimal promise wrapper around IndexedDB for the client delta cache
// (docs/performance.md). One database, an 'assets' store and a 'meta' key/value
// store for the sync tokens + identity. No dependency.
//
// v2 (tenant scope, docs/tenant-scope.md): the assets store is PARTITIONED per
// scope — records are keyed [ _scope, assetUUID ] where _scope is the cache
// partition key ('all' | 't:<tenantId>' | 'unassigned'). Each scope keeps its
// own rows + sync token, so switching back to a visited scope replays only its
// delta. The v1→v2 upgrade drops the old store (one full reload, then cached).
const DB_NAME = 'superb-tracking';
const DB_VERSION = 2;
let _dbp = null;

function open() {
  if (_dbp) return _dbp;
  _dbp = new Promise((resolve, reject) => {
    let req;
    try { req = indexedDB.open(DB_NAME, DB_VERSION); }
    catch (e) { return reject(e); }
    req.onupgradeneeded = () => {
      const db = req.result;
      // v1 kept assets keyed by assetUUID only — incompatible keyPath, recreate.
      if (db.objectStoreNames.contains('assets')) db.deleteObjectStore('assets');
      db.createObjectStore('assets', { keyPath: ['_scope', 'assetUUID'] });
      if (!db.objectStoreNames.contains('meta')) db.createObjectStore('meta');
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return _dbp;
}

export function idbAvailable() {
  return typeof indexedDB !== 'undefined';
}

// All records of one scope partition. [scope] ≤ key ≤ [scope, []] covers every
// [scope, '<uuid>'] because an array sorts after any string in IDB key order.
function scopeRange(scope) {
  return IDBKeyRange.bound([scope], [scope, []]);
}

export async function idbGetScope(store, scope) {
  const db = await open();
  return new Promise((res, rej) => {
    const req = db.transaction(store, 'readonly').objectStore(store).getAll(scopeRange(scope));
    req.onsuccess = () => res(req.result || []);
    req.onerror = () => rej(req.error);
  });
}

// Upsert items into one scope partition (stamps _scope on each row).
export async function idbBulkPut(store, items, scope) {
  if (!items?.length) return;
  const db = await open();
  return new Promise((res, rej) => {
    const tx = db.transaction(store, 'readwrite');
    const os = tx.objectStore(store);
    for (const it of items) os.put({ ...it, _scope: scope });
    tx.oncomplete = () => res();
    tx.onerror = () => rej(tx.error);
  });
}

export async function idbBulkDelete(store, keys, scope) {
  if (!keys?.length) return;
  const db = await open();
  return new Promise((res, rej) => {
    const tx = db.transaction(store, 'readwrite');
    const os = tx.objectStore(store);
    for (const k of keys) os.delete([scope, k]);
    tx.oncomplete = () => res();
    tx.onerror = () => rej(tx.error);
  });
}

// Clear ONE scope partition.
export async function idbClearScope(store, scope) {
  const db = await open();
  return new Promise((res, rej) => {
    const tx = db.transaction(store, 'readwrite');
    tx.objectStore(store).delete(scopeRange(scope));
    tx.oncomplete = () => res();
    tx.onerror = () => rej(tx.error);
  });
}

// Clear the whole store (identity change / logout).
export async function idbClear(store) {
  const db = await open();
  return new Promise((res, rej) => {
    const req = db.transaction(store, 'readwrite').objectStore(store).clear();
    req.onsuccess = () => res();
    req.onerror = () => rej(req.error);
  });
}

export async function idbGetMeta(key) {
  const db = await open();
  return new Promise((res, rej) => {
    const req = db.transaction('meta', 'readonly').objectStore('meta').get(key);
    req.onsuccess = () => res(req.result ?? null);
    req.onerror = () => rej(req.error);
  });
}

export async function idbSetMeta(key, value) {
  const db = await open();
  return new Promise((res, rej) => {
    const tx = db.transaction('meta', 'readwrite');
    tx.objectStore('meta').put(value, key);
    tx.oncomplete = () => res();
    tx.onerror = () => rej(tx.error);
  });
}

// Remove every meta entry whose key starts with the prefix (scope-token cleanup
// on identity change).
export async function idbClearMetaPrefix(prefix) {
  const db = await open();
  return new Promise((res, rej) => {
    const tx = db.transaction('meta', 'readwrite');
    const os = tx.objectStore('meta');
    const req = os.getAllKeys();
    req.onsuccess = () => {
      for (const k of req.result || []) {
        if (typeof k === 'string' && k.startsWith(prefix)) os.delete(k);
      }
    };
    tx.oncomplete = () => res();
    tx.onerror = () => rej(tx.error);
  });
}
